// The Talking Head's speech to speech (issue 238 part 2, live's `speech-to-speech-section`): one
// audio drives every actor. Its words and its checks are live's (`constants.ts`, `utils.ts`,
// `media-validation.ts`), read against the model's talking-head block.
import type { TalkingHeadCaps } from "../../../../api/generated/model";
import { lengthProblem } from "./cast";

/** Live's words (`speech-to-speech-section/constants.ts`, `audio-upload-entry`). */
export const SPEECH_TITLE = "Speech";
export const ENTRY_TITLE = "Upload audio or record your voice";
export const RECORD_TITLE = "Record Your Audio";
export const RECORD_DESCRIPTION =
  "Your recording will be used as the script for the generated video, so speak clearly and include everything you want the character to say.";
export const NO_DURATION = "Could not read audio duration.";

/** The audio every actor speaks: a library audio, uploaded, recorded or picked. */
export interface SpeechAudio {
  assetId: string;
  name: string;
  /** Its length, where the library knows it. */
  durationMs: number | null;
}

/** Live's format names for the block's audio types (its seed lists extensions). */
const EXTENSIONS: Readonly<Record<string, string>> = {
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/mp4": "m4a",
  "audio/aac": "aac",
};

/** The audio formats the model takes, in its block's order, by live's names. */
export function speechFormats(caps: TalkingHeadCaps | null): string[] {
  const names = (caps?.audio_mimes ?? []).map((mime) => EXTENSIONS[mime] ?? mime.split("/")[1] ?? mime);
  return [...new Set(names)];
}

/** Live's rule line (`formatAudioConstraintsHint`): the formats, then the length's range. */
export function speechHint(caps: TalkingHeadCaps | null): string {
  if (caps === null) return "";
  const min = caps.min_audio_seconds ?? null;
  const max = caps.max_audio_seconds ?? null;
  let range: string | null = null;
  if (min !== null && max !== null) range = `${min}–${max} sec`;
  else if (min !== null) range = `min ${min} sec`;
  else if (max !== null) range = `max ${max} sec`;
  const formats = speechFormats(caps);
  return [formats.length === 0 ? null : formats.join(", "), range]
    .filter((part): part is string => part !== null)
    .join(", ");
}

/**
 * What is wrong with an audio for the model, in live's words and order (`validateSpeechFile`):
 * the format (not for a recording, which is always WAV), then the length, or that it could not
 * be read where the model bounds it.
 */
export function speechProblems(
  file: Blob,
  seconds: number | null,
  caps: TalkingHeadCaps | null,
  recorded = false,
): string[] {
  if (caps === null) return [];
  if (!recorded && caps.audio_mimes.length > 0 && !caps.audio_mimes.includes(file.type)) {
    return [`Unsupported format. Allowed: ${speechFormats(caps).join(", ")}.`];
  }
  const bounded = (caps.min_audio_seconds ?? null) !== null || (caps.max_audio_seconds ?? null) !== null;
  if (!bounded) return [];
  if (seconds === null) return [NO_DURATION];
  const problem = lengthProblem(seconds * 1000, {
    min_seconds: caps.min_audio_seconds ?? null,
    max_seconds: caps.max_audio_seconds ?? null,
  });
  return problem === null ? [] : [problem];
}
